import { type ReactNode, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate, useParams } from 'react-router-dom'
import { core } from '@musinsa/pbo-core'
import { PboScope } from '@/components/pbo/PboScope'
import { notice } from '@/components/pbo/notice'
import { apiErrorText } from '@/components/pbo/apiErrorText'
import type { BoardPostDetail } from '@/services/board/types'
import { useCreateBoardPost, useGetBoardPost, useUpdateBoardPost } from '@/services/board/useBoardQueries'
import { useGetShipperOptions } from '@/services/inventory/stockLedger/useStockLedgerQueries'
import { AuthStorage } from '@/utils/storage/AuthStorage'
import { BoardEditor } from './components/BoardEditor'
import { BOARD_BASE_PATH, BOARD_CATEGORIES, MAX_ATTACHMENTS, NOTICE_AUTH_CODES, NOTICE_CATEGORY, NOTICE_TARGET_ALL, TITLE_MAX_LENGTH } from './consts'
import { boardFormSchema, type BoardFormValues } from './schemas/boardFormSchema'
import { sanitizeBoardHtml } from './sanitizeBoardHtml'
import { editFormGate, toCreateReq, toUpdateReq, uploadShipperFor } from './boardFormPayload'
import { uploadBoardAttachment } from './uploadBoardAttachment'

/** 새로 올린 첨부 — 키는 올린 화주 경로(`{env}/{shipper}/…`)에 묶인다. */
interface NewAttachment {
  uid: string
  fileName: string
  shipper: string
  key?: string
  failed?: boolean
}

const canWriteNotice = (): boolean => NOTICE_AUTH_CODES.includes(String(AuthStorage.user.get().auth ?? ''))
/** 서버가 일반 글 화주로 쓰는 소속 화주 — 화주 전환은 `shipper_code`·`company` 만 바꾸고 `SPR_NM` 은 로그인 값 그대로다(MainHeader). */
const homeShipper = (): string => String(AuthStorage.user.get().SPR_NM ?? '')

let uidSeq = 0

/** pbo-core FormItem 에는 오류 문구 자리가 없어 입력 아래에 붙인다. */
function Field({ label, required, error, wide, children }: Readonly<{ label: string; required?: boolean; error?: string; wide?: boolean; children: ReactNode }>) {
  return (
    <core.FormItem label={label} required={required} labelPosition="top" boxedItem={!wide}>
      <core.Flex vertical gap={4} style={{ width: '100%' }}>
        {children}
        {error && <core.Typography.Text type="danger">{error}</core.Typography.Text>}
      </core.Flex>
    </core.FormItem>
  )
}

function BoardForm({ post }: Readonly<{ post?: BoardPostDetail }>) {
  const navigate = useNavigate()
  const isEdit = !!post
  const wasNotice = !!post && (post.notice || post.category === NOTICE_CATEGORY)
  const createPost = useCreateBoardPost()
  const updatePost = useUpdateBoardPost(post?.id ?? NaN)
  const { options: shipperOptions } = useGetShipperOptions()

  const { control, handleSubmit, watch, setValue, formState } = useForm<BoardFormValues>({
    resolver: zodResolver(boardFormSchema),
    // 수정 본문도 거른 뒤 에디터에 넣는다 — Quill 1 은 붙여 넣을 HTML 을 문서에 붙은 컨테이너에 innerHTML 로 넣는다(clipboard.convert).
    defaultValues: { category: post?.category ?? '', title: post?.title ?? '', content: sanitizeBoardHtml(post?.content), noticeTarget: undefined, isEdit },
  })
  const category = watch('category')
  const noticeTarget = watch('noticeTarget')
  const isNotice = category === NOTICE_CATEGORY

  // 수정은 공지 여부를 바꿀 수 없다(서버 400) — 공지 글은 공지사항만, 일반 글은 공지사항을 뺀 분류만.
  const categoryOptions = useMemo(() => {
    const visible = BOARD_CATEGORIES.filter((c) => (isEdit ? (c === NOTICE_CATEGORY) === wasNotice : c !== NOTICE_CATEGORY || canWriteNotice()))
    return visible.map((c) => ({ value: c, label: c }))
  }, [isEdit, wasNotice])

  const [keepIndexes, setKeepIndexes] = useState<number[]>(() => post?.files.map((f) => f.index) ?? [])
  const [uploads, setUploads] = useState<NewAttachment[]>([])
  const uploading = uploads.some((u) => !u.key && !u.failed)

  const uploadShipper = (): string | undefined => uploadShipperFor({ editShipper: post?.shipperCode, category, noticeTarget, homeShipper: homeShipper() })

  const addFiles = (files: File[]) => {
    const shipper = uploadShipper()
    if (!shipper) {
      notice.invalid(isNotice ? '공지사항 대상을 먼저 선택해 주세요.' : '소속 화주를 확인할 수 없어 첨부파일을 올릴 수 없습니다. 다시 로그인해 주세요.')
      return
    }
    const room = MAX_ATTACHMENTS - keepIndexes.length - uploads.filter((u) => !u.failed).length
    if (files.length > room) {
      notice.invalid(`첨부파일은 ${MAX_ATTACHMENTS}개까지 올릴 수 있습니다.`)
      return
    }
    files.forEach((file) => {
      uidSeq += 1
      const uid = `u${uidSeq}`
      setUploads((list) => [...list, { uid, fileName: file.name, shipper }])
      uploadBoardAttachment(shipper, file).then(
        (key) => setUploads((list) => list.map((u) => (u.uid === uid ? { ...u, key } : u))),
        (e) => {
          setUploads((list) => list.map((u) => (u.uid === uid ? { ...u, failed: true } : u)))
          notice.failure(apiErrorText(e, `${file.name} 을(를) 올리지 못했습니다. 다시 시도해 주세요.`))
        },
      )
    })
  }

  const onSubmit = handleSubmit(async (values) => {
    if (uploading) {
      notice.invalid('첨부파일을 올리는 중입니다. 끝난 뒤 저장해 주세요.')
      return
    }
    const ok = uploads.filter((u): u is NewAttachment & { key: string } => !!u.key)
    // 공지 대상을 바꾸면 이미 올린 첨부의 화주 경로가 달라져 서버가 거절한다 — 저장 전에 알린다.
    if (ok.some((u) => u.shipper !== uploadShipper())) {
      notice.invalid('공지사항 대상을 바꾼 뒤에는 첨부파일을 지우고 다시 올려 주세요.')
      return
    }
    const attachmentKeys = ok.map((u) => u.key)
    try {
      if (isEdit) {
        await updatePost.mutateAsync(toUpdateReq(values, attachmentKeys, keepIndexes))
        notice.success('수정했습니다.')
        navigate(`${BOARD_BASE_PATH}/${post.id}`)
      } else {
        const id = await createPost.mutateAsync(toCreateReq(values, attachmentKeys))
        notice.success('등록했습니다.')
        navigate(`${BOARD_BASE_PATH}/${id}`)
      }
    } catch (e) {
      notice.failure(apiErrorText(e, '저장하지 못했습니다. 다시 시도해 주세요.'))
    }
  })

  const errors = formState.errors
  const saving = createPost.isPending || updatePost.isPending

  return (
    <core.Flex vertical gap={16}>
      <core.Flex justify="space-between" align="center">
        <core.Typography.Title level={4} style={{ margin: 0 }}>
          {isEdit ? '게시글 수정' : '게시글 등록'}
        </core.Typography.Title>
        <core.Space>
          <core.Button onClick={() => navigate(isEdit ? `${BOARD_BASE_PATH}/${post.id}` : BOARD_BASE_PATH)}>취소</core.Button>
          <core.Button type="primary" loading={saving} disabled={uploading} onClick={onSubmit}>
            저장
          </core.Button>
        </core.Space>
      </core.Flex>

      <core.Card>
        <core.Flex vertical gap={16}>
          <Field label="분류" required error={errors.category?.message}>
            <Controller
              control={control}
              name="category"
              render={({ field }) => (
                <core.Select
                  value={field.value || undefined}
                  onChange={(v: string) => {
                    field.onChange(v)
                    if (v !== NOTICE_CATEGORY) setValue('noticeTarget', undefined)
                  }}
                  options={categoryOptions}
                  placeholder="분류를 선택하세요"
                  style={{ width: 240 }}
                />
              )}
            />
          </Field>

          {isNotice && !isEdit && (
            <Field label="공지사항 대상" required error={errors.noticeTarget?.message}>
              <Controller
                control={control}
                name="noticeTarget"
                render={({ field }) => (
                  <core.Select
                    showSearch
                    optionFilterProp="label"
                    value={field.value}
                    onChange={field.onChange}
                    options={[{ value: NOTICE_TARGET_ALL, label: '전체공지' }, ...shipperOptions]}
                    placeholder="전체공지 또는 화주를 선택하세요"
                    style={{ width: 320 }}
                  />
                )}
              />
            </Field>
          )}

          <Field label="제목" required error={errors.title?.message}>
            <Controller control={control} name="title" render={({ field }) => <core.Input {...field} maxLength={TITLE_MAX_LENGTH} placeholder="제목을 입력하세요" />} />
          </Field>

          <Field label="내용" required wide error={errors.content?.message}>
            <Controller control={control} name="content" render={({ field }) => <BoardEditor value={field.value} onChange={field.onChange} onImageFiles={addFiles} />} />
          </Field>

          <Field label={`첨부파일 (최대 ${MAX_ATTACHMENTS}개 · 이미지는 본문에 붙여넣어도 첨부로 올라갑니다)`} wide>
            <core.Flex vertical gap={4} align="flex-start">
              {post?.files
                .filter((f) => keepIndexes.includes(f.index))
                .map((f) => (
                  <core.Space key={`keep-${f.index}`}>
                    <core.Typography.Text>{f.fileName}</core.Typography.Text>
                    <core.Button size="small" type="link" danger onClick={() => setKeepIndexes((list) => list.filter((i) => i !== f.index))}>
                      삭제
                    </core.Button>
                  </core.Space>
                ))}
              {uploads.map((u) => (
                <core.Space key={u.uid}>
                  <core.Typography.Text type={u.failed ? 'danger' : undefined}>
                    {u.fileName}
                    {u.failed ? ' (실패)' : !u.key ? ' (올리는 중)' : ''}
                  </core.Typography.Text>
                  <core.Button size="small" type="link" danger onClick={() => setUploads((list) => list.filter((x) => x.uid !== u.uid))}>
                    삭제
                  </core.Button>
                </core.Space>
              ))}
              <core.Button
                size="small"
                onClick={() => {
                  const input = document.createElement('input')
                  input.type = 'file'
                  input.multiple = true
                  input.onchange = () => addFiles(Array.from(input.files ?? []))
                  input.click()
                }}
              >
                파일 선택
              </core.Button>
            </core.Flex>
          </Field>
        </core.Flex>
      </core.Card>
    </core.Flex>
  )
}

function EditForm({ postId }: Readonly<{ postId: number }>) {
  const navigate = useNavigate()
  const { data: post, isLoading, isFetching, isError, refetch } = useGetBoardPost(postId, { reuseCache: true })
  const gate = editFormGate({ isLoading, isFetching, isError, post })

  if (gate === 'loading') return <core.Flex justify="center" style={{ padding: 48 }}>불러오는 중…</core.Flex>
  if (gate !== 'ready' || !post) {
    const message = gate === 'error' ? '게시글을 불러오지 못했습니다.' : gate === 'notEditable' ? '수정할 수 없는 게시글입니다.' : '게시글을 찾을 수 없습니다.'
    return (
      <core.Flex vertical align="center" gap={16} style={{ padding: 48 }}>
        <core.Empty description={message} />
        <core.Space>
          <core.Button onClick={() => navigate(BOARD_BASE_PATH)}>목록</core.Button>
          {gate === 'error' && <core.Button onClick={() => refetch()}>다시 시도</core.Button>}
        </core.Space>
      </core.Flex>
    )
  }
  return <BoardForm post={post} />
}

export default function BoardFormPage() {
  const { id } = useParams()
  return <PboScope>{id ? <EditForm postId={Number(id)} /> : <BoardForm />}</PboScope>
}
